import { makeAutoObservable } from 'mobx';
import { kpiService, KPIData } from '../services/kpiService';

export interface DashboardStats {
  totalRequests: number;
  weeklyRequests: number;
  totalServiceProviders: number;
  totalServiceUsers: number;
}

export interface MonthlyRequestData {
  month: string;
  requests: number;
  cumulative: number;
}

export class DashboardViewModel {
  stats: DashboardStats = {
    totalRequests: 0,
    weeklyRequests: 0,
    totalServiceProviders: 0,
    totalServiceUsers: 0
  };

  monthlyData: MonthlyRequestData[] = [];
  kpiData: KPIData | null = null;
  isLoading = false;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  // Format number to two digits (e.g., 8 -> "08")
  formatNumber(num: number): string {
    return num.toString().padStart(2, '0');
  }

  // Format currency
  formatCurrency(amount: string | number): string {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(num);
  }

  // Generate monthly data - returns empty array since growth trends are no longer available
  generateMonthlyDataFromKPIs(): MonthlyRequestData[] {
    return [];
  }

  // Fetch dashboard statistics from KPI API
  async fetchDashboardStats() {
    this.isLoading = true;
    this.error = null;
    
    try {
      const response = await kpiService.getKPIs();
      
      if (response.success && response.data) {
        this.kpiData = response.data;
        
        // Update legacy stats for backward compatibility
        this.stats = {
          totalRequests: response.data.overview.totalServiceRequests,
          weeklyRequests: 0, // No longer available from API
          totalServiceProviders: response.data.overview.totalServiceProviders,
          totalServiceUsers: response.data.overview.totalServiceUsers
        };

        // Generate monthly data from KPI growth trends
        this.monthlyData = this.generateMonthlyDataFromKPIs();
      } else {
        throw new Error('Invalid response from KPI API');
      }
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'Failed to fetch dashboard stats';
      console.error('Error fetching KPI data:', error);
    } finally {
      this.isLoading = false;
    }
  }

  // Get formatted stats for display
  get formattedStats() {
    return {
      totalRequests: this.formatNumber(this.stats.totalRequests),
      weeklyRequests: this.formatNumber(this.stats.weeklyRequests),
      totalServiceProviders: this.formatNumber(this.stats.totalServiceProviders),
      totalServiceUsers: this.formatNumber(this.stats.totalServiceUsers)
    };
  }

  // Get chart data for monthly progress
  get chartData() {
    return this.monthlyData.map(item => ({
      month: item.month,
      requests: item.requests,
      cumulative: item.cumulative
    }));
  }

  // Get maximum value for chart scaling
  get maxChartValue() {
    if (this.monthlyData.length === 0) return 100;
    return Math.max(...this.monthlyData.map(item => item.cumulative));
  }

  // Initialize dashboard
  initialize() {
    this.fetchDashboardStats();
  }
}
